import { useSyncExternalStore } from 'react';
import { isSpriteLoaded, subscribeSpriteReady } from '../../../sprite-loader';

export interface IconProps {
    /** Symbol id inside the SVG sprite. */
    id: string;
    /** Width and height in pixels. */
    size?: number;
    /** SVG fill (also propagated to `color` for `currentColor`-based symbols). */
    fill?: string;
    /**
     * Optional accessible label. When provided, the icon is exposed to AT;
     * when omitted, the icon is hidden from AT (presentational).
     */
    title?: string;
    className?: string;
}

/**
 * Subscribes a component to the sprite-ready signal so that <use> references
 * which were inserted before the sprite arrived re-resolve once it does.
 */
const useSpriteReady = (): boolean =>
    useSyncExternalStore(
        subscribeSpriteReady,
        isSpriteLoaded,
        // SSR snapshot — we never render a sprite-dependent UI server-side.
        () => false
    );

export const Icon = ({ id, size = 24, fill = 'currentColor', title, className }: IconProps) => {
    // Trigger a re-render the moment the sprite becomes available so the
    // <use> element re-resolves to the in-document <symbol>.
    useSpriteReady();

    const accessible = Boolean(title);

    return (
        <svg
            width={size}
            height={size}
            fill={fill}
            color={fill}
            className={className}
            role={accessible ? 'img' : undefined}
            aria-label={accessible ? title : undefined}
            aria-hidden={accessible ? undefined : true}
            focusable="false"
        >
            {accessible && <title>{title}</title>}
            <use href={`#${id}`} />
        </svg>
    );
};
